'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import {
  MessageSquare,
  ThumbsUp,
  Bookmark,
  Share2,
  Pin,
  ChevronDown,
  Send,
  Image,
  Link as LinkIcon,
  Paperclip,
} from 'lucide-react';

const topicChips = ['All', 'AI & ML', 'Data Science', 'Health Informatics', 'Coding for Kids', 'Career', 'Resources'];

const threads = [
  {
    id: 'thread-001',
    author: 'Dr. Priya Sharma',
    authorRole: 'Educator',
    avatar: 'PS',
    avatarBg: 'bg-emerald-500',
    topic: 'Health Informatics',
    topicClass: 'badge-topic-health',
    title: 'Best practices for de-identifying patient data before ML model training',
    preview: "I've been working on a clinical NLP project and we're hitting some challenges with HIPAA-compliant de-identification. Has anyone used Microsoft Presidio or similar tools in a health informatics pipeline? Would love to hear about your workflows...",
    upvotes: 47,
    replies: 23,
    time: '2h ago',
    pinned: true,
    bookmarked: false,
    tags: ['HIPAA', 'NLP', 'De-identification'],
    upvoted: false,
  },
  {
    id: 'thread-002',
    author: 'Taiwo Adeyemi',
    authorRole: 'Learner',
    avatar: 'TA',
    avatarBg: 'bg-purple-500',
    topic: 'AI & ML',
    topicClass: 'badge-topic-ai',
    title: 'Confused about when to use LSTM vs Transformer for time-series forecasting',
    preview: "I'm building a model to predict hospital readmission rates over time. I initially went with LSTM but a colleague suggested switching to a Transformer architecture. The course covers both but doesn't directly compare them for this use case...",
    upvotes: 31,
    replies: 18,
    time: '4h ago',
    pinned: false,
    bookmarked: true,
    tags: ['LSTM', 'Transformer', 'Time Series'],
    upvoted: true,
  },
  {
    id: 'thread-003',
    author: 'Ngozi Eze',
    authorRole: 'Learner',
    avatar: 'NE',
    avatarBg: 'bg-blue-500',
    topic: 'Data Science',
    topicClass: 'badge-topic-data',
    title: 'Share your favourite free datasets for practising data cleaning',
    preview: "I find that the best way to really learn Pandas is to work with genuinely messy real-world data. I'll start: the WHO Global Health Observatory data is incredible for health informatics practice — inconsistent formats, missing values, mixed encodings...",
    upvotes: 89,
    replies: 41,
    time: '6h ago',
    pinned: false,
    bookmarked: false,
    tags: ['Datasets', 'Pandas', 'Data Cleaning'],
    upvoted: false,
  },
  {
    id: 'thread-004',
    author: 'Jamie Okonkwo',
    authorRole: 'Educator',
    avatar: 'JO',
    avatarBg: 'bg-amber-500',
    topic: 'Coding for Kids',
    topicClass: 'badge-topic-kids',
    title: 'Tips for keeping 10–12 year olds engaged during 45-minute coding sessions',
    preview: "I teach the Scratch and Python for Kids courses here and I'm always looking for engagement strategies. What I've found works: 15-min sprints, mini-challenges with visible output, and letting them pick their own project themes. What works for you?",
    upvotes: 62,
    replies: 29,
    time: '1d ago',
    pinned: false,
    bookmarked: false,
    tags: ['Teaching', 'Engagement', 'Scratch'],
    upvoted: false,
  },
  {
    id: 'thread-005',
    author: 'Rafael Mendes',
    authorRole: 'Learner',
    avatar: 'RM',
    avatarBg: 'bg-rose-500',
    topic: 'AI & ML',
    topicClass: 'badge-topic-ai',
    title: 'My experience deploying the capstone ML model to AWS Lambda — what I learnt',
    preview: "Just completed the ML Fundamentals capstone and wanted to share my deployment experience. I went with AWS Lambda + API Gateway instead of the suggested Flask approach. Cold start times were a real issue with scikit-learn models — here's how I solved it...",
    upvotes: 54,
    replies: 15,
    time: '1d ago',
    pinned: false,
    bookmarked: false,
    tags: ['Deployment', 'AWS', 'Production'],
    upvoted: false,
  },
  {
    id: 'thread-006',
    author: 'Amara Diallo',
    authorRole: 'Learner',
    avatar: 'AD',
    avatarBg: 'bg-indigo-500',
    topic: 'Health Informatics',
    topicClass: 'badge-topic-health',
    title: 'Understanding HL7 FHIR R4 vs DSTU2 — practical migration considerations',
    preview: "Our hospital is planning to migrate our EHR integration from FHIR DSTU2 to R4. The course covers FHIR basics but doesn't go deep on migration. Has anyone been through this? Particularly interested in how breaking changes in the Patient resource affected your downstream systems...",
    upvotes: 28,
    replies: 12,
    time: '2d ago',
    pinned: false,
    bookmarked: false,
    tags: ['FHIR', 'EHR', 'Migration'],
    upvoted: false,
  },
  {
    id: 'thread-007',
    author: 'Fatima Al-Rashid',
    authorRole: 'Educator',
    avatar: 'FA',
    avatarBg: 'bg-teal-500',
    topic: 'Data Science',
    topicClass: 'badge-topic-data',
    title: '[Resource] Curated list of open-access health informatics research papers for 2026',
    preview: "I've compiled 40+ freely accessible papers covering EHR analysis, clinical NLP, predictive modelling in ICU settings, and more. All are from 2024–2026 and include implementation code where available. Sharing the full list below...",
    upvotes: 112,
    replies: 34,
    time: '3d ago',
    pinned: false,
    bookmarked: true,
    tags: ['Research', 'Papers', 'Open Access'],
    upvoted: false,
  },
  {
    id: 'thread-008',
    author: 'Kofi Mensah',
    authorRole: 'Educator',
    avatar: 'KM',
    avatarBg: 'bg-orange-500',
    topic: 'Data Science',
    topicClass: 'badge-topic-data',
    title: 'How to interpret a confusion matrix when classes are severely imbalanced',
    preview: "A common mistake I see learners make is optimising for accuracy when their dataset is 95% negative class. This thread covers: why accuracy is misleading, how to read precision/recall/F1, and when to use ROC-AUC vs PR-AUC for imbalanced classification...",
    upvotes: 76,
    replies: 22,
    time: '4d ago',
    pinned: false,
    bookmarked: false,
    tags: ['Imbalanced Data', 'Metrics', 'Classification'],
    upvoted: false,
  },
];

export default function CommunityFeed() {
  const [activeChip, setActiveChip] = useState('All');
  const [sort, setSort] = useState('Most Recent');
  const [postText, setPostText] = useState('');
  const [postTitle, setPostTitle] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [threadStates, setThreadStates] = useState<Record<string, { upvoted: boolean; bookmarked: boolean; upvotes: number }>>(
    Object.fromEntries(threads.map((t) => [t.id, { upvoted: t.upvoted, bookmarked: t.bookmarked, upvotes: t.upvotes }]))
  );

  const handleUpvote = (id: string) => {
    setThreadStates((prev) => {
      const cur = prev[id];
      return { ...prev, [id]: { ...cur, upvoted: !cur.upvoted, upvotes: cur.upvoted ? cur.upvotes - 1 : cur.upvotes + 1 } };
    });
  };

  const handleBookmark = (id: string) => {
    setThreadStates((prev) => {
      const cur = prev[id];
      toast(cur.bookmarked ? 'Removed from bookmarks' : 'Saved to bookmarks');
      return { ...prev, [id]: { ...cur, bookmarked: !cur.bookmarked } };
    });
  };

  const handlePost = () => {
    if (!postTitle.trim() || !postText.trim()) {
      toast.error('Please add a title and content to your post');
      return;
    }
    // Backend integration point: POST /api/community/threads with { title, body, topic }
    toast.success('Post published!', { description: 'Your question is now visible to the community.' });
    setPostTitle('');
    setPostText('');
    setComposerOpen(false);
  };

  const filtered = threads.filter((t) => {
    if (activeChip === 'All') return true;
    const map: Record<string, string> = {
      'AI & ML': 'AI & ML',
      'Data Science': 'Data Science',
      'Health Informatics': 'Health Informatics',
      'Coding for Kids': 'Coding for Kids',
    };
    return t.topic === (map[activeChip] || activeChip) || t.tags.includes(activeChip);
  });

  return (
    <div className="flex-1 min-w-0 space-y-4">
      {/* Post composer */}
      <div className="card p-4">
        {!composerOpen ? (
          <button
            onClick={() => setComposerOpen(true)}
            className="w-full flex items-center gap-3 text-left"
          >
            <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              SA
            </div>
            <div className="flex-1 px-4 py-2.5 rounded-xl bg-muted text-sm text-muted-foreground hover:bg-muted/70 transition-colors cursor-text">
              Share a question, resource, or insight with the community...
            </div>
          </button>
        ) : (
          <div className="space-y-3 fade-in">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                SA
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={postTitle}
                  onChange={(e) => setPostTitle(e.target.value)}
                  placeholder="Post title (be specific — good titles get more helpful replies)"
                  className="input-field w-full text-sm font-semibold"
                />
              </div>
            </div>
            <textarea
              value={postText}
              onChange={(e) => setPostText(e.target.value)}
              placeholder="Share context, what you've tried, and what you're looking for..."
              className="input-field w-full min-h-[100px] resize-none ml-12"
            />
            <div className="flex items-center justify-between ml-12">
              <div className="flex items-center gap-2">
                <button className="btn-ghost py-1.5 px-2.5 text-xs"><Image size={13} /> Image</button>
                <button className="btn-ghost py-1.5 px-2.5 text-xs"><LinkIcon size={13} /> Link</button>
                <button className="btn-ghost py-1.5 px-2.5 text-xs"><Paperclip size={13} /> File</button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setComposerOpen(false)} className="btn-ghost text-sm">Cancel</button>
                <button onClick={handlePost} className="btn-primary py-2 text-sm">
                  <Send size={13} />
                  Post
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {topicChips.map((chip) => (
            <button
              key={`community-chip-${chip}`}
              onClick={() => setActiveChip(chip)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 ${
                activeChip === chip
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
              }`}
            >
              {chip}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="input-field py-1.5 text-xs w-auto min-w-[140px]"
          >
            {['Most Recent', 'Most Upvoted', 'Most Replies', 'Unanswered'].map((s) => (
              <option key={`comm-sort-${s}`}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Thread list */}
      <div className="space-y-3">
        {filtered.map((thread) => {
          const state = threadStates[thread.id];
          return (
            <div key={thread.id} className={`card p-4 hover:shadow-card-hover transition-all duration-200 ${thread.pinned ? 'border-primary/30 bg-secondary/20' : ''}`}>
              {/* Thread header */}
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-full ${thread.avatarBg} text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5`}>
                  {thread.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-foreground">{thread.author}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${thread.authorRole === 'Educator' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {thread.authorRole}
                    </span>
                    <span className={thread.topicClass}>{thread.topic}</span>
                    {thread.pinned && (
                      <span className="flex items-center gap-1 text-xs text-primary font-medium">
                        <Pin size={10} /> Pinned
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">{thread.time}</span>
                  </div>

                  <h3 className="text-sm font-semibold text-foreground leading-snug mb-1.5 hover:text-primary cursor-pointer transition-colors">
                    {thread.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-2">
                    {thread.preview}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {thread.tags.map((tag) => (
                      <span key={`tag-${thread.id}-${tag}`} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-md">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleUpvote(thread.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
                        state.upvoted
                          ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <ThumbsUp size={12} className={state.upvoted ? 'fill-primary' : ''} />
                      <span className="font-tabular">{state.upvotes}</span>
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                      <MessageSquare size={12} />
                      <span className="font-tabular">{thread.replies}</span>
                    </button>
                    <button
                      onClick={() => handleBookmark(thread.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
                        state.bookmarked ? 'text-accent' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Bookmark size={12} className={state.bookmarked ? 'fill-accent' : ''} />
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors ml-auto">
                      <Share2 size={12} />
                      Share
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Load more */}
      <button className="btn-outline w-full py-3 text-sm">
        <ChevronDown size={16} />
        Load more discussions
      </button>
    </div>
  );
}